import { z } from 'zod';
/**
 * Промпты и конфигурация агента — с версиями.
 *
 * Главное решение этого шага формулируется одной фразой: **версия
 * неизменяема, а «текущая» — это указатель**. Правка промпта не меняет
 * существующую версию, а создаёт следующую; публикация переставляет
 * указатель на одну из версий; откат — это та же публикация, только
 * указателем назад. Отдельной операции «откат» в системе нет, и это
 * не экономия, а следствие: откат, устроенный как отдельная операция,
 * почти всегда оказывается «копированием старого текста в новую версию»,
 * и тогда история перестаёт отвечать на вопрос «а что именно работало
 * в прошлый вторник».
 *
 * Второе решение — **версия агента ссылается на версию промпта, а не на
 * промпт**. Иначе правка промпта молча меняла бы поведение всех агентов,
 * которые его используют, и запись «диалог шёл на версии 3» стала бы
 * ложью: конфигурация та же, а текст инструкции другой. Цена честности —
 * лишний шаг: опубликовав новую версию промпта, нужно создать новую
 * версию агента. Поэтому список агентов, оставшихся на прежней версии
 * промпта, отдаётся прямо в ответе на публикацию.
 */
// ─── промпты ───
/**
 * Роль промпта внутри версии агента.
 *
 * Набор задан целиком сейчас, хотя нужен из него один `system`. Причина
 * в том, что роль — часть первичного ключа связи, и добавление значения
 * позже означало бы миграцию. Перечислить заранее то, что уже описано в
 * архитектуре, дешевле.
 */
/**
 * Роли промптов.
 *
 * `validation` и `readiness` легко перепутать, поэтому разница здесь, а не
 * в чьей-то памяти. `validation` смотрит на **ответ агента** и решает,
 * годится ли он к отправке: факты, действия, формат. `readiness` смотрит
 * на **весь разговор** и решает, дозрел ли клиент до звонка продавца.
 * Разный вход, разный исход, разные промпты.
 *
 * В старой системе это были два отдельных узла: `supervisor` — наш
 * `validation`, и `conditional_human_feedback_validator` — наш `readiness`.
 * Второй в техническом справочнике отсутствовал; роль восстановлена по
 * снимкам интерфейса 22.09.2026, см. `docs/reference/old-admin/`.
 */
export const PROMPT_ROLES = [
    'system',
    'validation',
    'readiness',
    'followup_step',
    'followup_last',
];
export const promptRoleSchema = z.enum(PROMPT_ROLES);
export const PROMPT_ROLE_LABELS = {
    system: 'Системная инструкция',
    validation: 'Проверка ответа',
    readiness: 'Готовность к звонку',
    followup_step: 'Шаг follow-up',
    followup_last: 'Последний шаг follow-up',
};
/** Роль, без которой версию агента публиковать нельзя. */
export const REQUIRED_PROMPT_ROLES = ['system'];
export const PROMPT_BODY_MAX = 40_000;
export const promptVersionSchema = z.object({
    id: z.uuid(),
    promptId: z.uuid(),
    /** Номер версии, сквозной внутри промпта. Всегда растёт, дыр не бывает. */
    version: z.number().int().positive(),
    body: z.string(),
    /**
     * Хэш содержимого. Отдаётся наружу не для проверки, а чтобы интерфейс
     * мог отличить «версии совпадают» от «версии разные» без загрузки
     * обоих текстов целиком.
     */
    contentHash: z.string(),
    /** Что изменилось — заполняет человек. Пустая строка допустима. */
    note: z.string(),
    createdByUserId: z.uuid().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** Эта версия сейчас опубликована. */
    isPublished: z.boolean(),
});
/** Версия без тела — для списков, где сотня текстов по сорок тысяч знаков не нужна. */
export const promptVersionSummarySchema = promptVersionSchema.omit({ body: true }).extend({
    /** Длина тела в знаках: даёт понять масштаб правки, не загружая текст. */
    bodyLength: z.number().int().nonnegative(),
});
export const promptSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string(),
    tags: z.array(z.string()),
    /** Опубликованная версия. null — промпт создан, но ни одна версия не опубликована. */
    publishedVersion: promptVersionSummarySchema.nullable(),
    /** Номер последней созданной версии. Может быть больше опубликованного — это откат. */
    latestVersion: z.number().int().nonnegative(),
    versionCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const promptDetailSchema = promptSchema.extend({
    versions: z.array(promptVersionSummarySchema),
    /** Тело опубликованной версии, либо последней, если ничего не опубликовано. */
    currentBody: z.string(),
});
export const createPromptInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).default(''),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    body: z.string().min(1).max(PROMPT_BODY_MAX),
    note: z.string().trim().max(300).default(''),
    /**
     * Опубликовать сразу. По умолчанию да: промпт без опубликованной версии
     * ничего не делает, и создание «про запас» — редкий случай, ради которого
     * не стоит заставлять человека делать два действия вместо одного.
     */
    publish: z.boolean().default(true),
});
export const updatePromptInputSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export const createPromptVersionInputSchema = z.object({
    body: z.string().min(1).max(PROMPT_BODY_MAX),
    note: z.string().trim().max(300).default(''),
    publish: z.boolean().default(false),
});
export const publishInputSchema = z.object({ versionId: z.uuid() });
/**
 * Кого задело изменение промпта.
 *
 * Отдаётся в ответ на публикацию версии промпта и отвечает на вопрос,
 * который иначе всплыл бы через неделю в форме «почему агент отвечает
 * по-старому». Ответ прямой: потому что его опубликованная версия
 * закреплена за прежней версией промпта, и это сделано нарочно.
 */
export const staleAgentSchema = z.object({
    agentId: z.uuid(),
    agentName: z.string(),
    agentVersion: z.number().int().positive(),
    role: promptRoleSchema,
    pinnedPromptVersion: z.number().int().positive(),
});
export const promptPublishResultSchema = z.object({
    version: promptVersionSummarySchema,
    staleAgents: z.array(staleAgentSchema),
});
/**
 * Результат создания версии.
 *
 * `created: false` означает, что тело совпало с последней версией и новая
 * не появилась. Это не ошибка: редактор, сохраняющий по уходу фокуса,
 * иначе плодил бы версии без изменений, и история становилась бы
 * нечитаемой именно тогда, когда она нужна.
 *
 * `staleAgents` здесь, а не только в ответе на отдельную публикацию.
 * Причина нашлась на живом прогоне в браузере: «сохранить и опубликовать»
 * — основной путь, а публикация из истории версий нужна для отката, и
 * предупреждение об отставших агентах появлялось только на редком из
 * двух. Одна и та же публикация обязана объяснять себя одинаково,
 * какой бы кнопкой её ни начали.
 */
export const promptVersionResultSchema = z.object({
    version: promptVersionSummarySchema,
    created: z.boolean(),
    published: z.boolean(),
    staleAgents: z.array(staleAgentSchema),
});
// ─── конфигурация агента ───
/**
 * Номер формата конфигурации.
 *
 * Конфигурация хранится в JSONB одним объектом, а не двадцатью столбцами:
 * версия читается целиком, и запросов вида «все версии с temperature выше
 * 0,7» не бывает. Но у такого хранения есть ловушка, из-за которой и нужен
 * этот номер. Если разбирать старую строку схемой с значениями по
 * умолчанию, строка молча получит поле, которого в ней не было, — и
 * неизменяемая версия начнёт отвечать на вопрос «с чем работал агент»
 * тем, что придумано сегодня. Поэтому схема ниже не имеет ни одного
 * `.default()`, значения по умолчанию заданы отдельной константой и
 * записываются в момент создания версии, а несовпадение номера формата
 * обрабатывается явной функцией переноса.
 */
export const AGENT_CONFIG_VERSION = 6;
export const historyModeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('all') }),
    z.object({ kind: z.literal('lastMessages'), count: z.number().int().min(1).max(500) }),
    z.object({ kind: z.literal('lastMinutes'), minutes: z.number().int().min(1).max(20_160) }),
]);
/**
 * Настройки «раскрывать ли клиенту, что он говорит с AI» здесь нет, и это
 * решение заказчика от 22.09.2026, а не упущение. Поведение задаётся
 * текстом системной инструкции, как и всё остальное, что агент говорит
 * о себе, — отдельного переключателя под это не заводится.
 *
 * Одно следствие стоит помнить при выходе на новые рынки: в ЕС статья 50
 * AI Act требует раскрытия, и там это перестаёт быть вопросом настройки.
 * Текущие рынки — LATAM, требования другие.
 */
export const agentConfigSchema = z.object({
    /** Имя модели строкой, без перечисления: список у OpenAI меняется чаще релизов. */
    model: z.string().trim().min(1).max(100),
    /**
     * Модель для служебных вызовов — проверки ответа и проверки готовности
     * клиента к звонку. `null` означает «та же, что отвечает клиенту».
     *
     * Поле появилось после сравнения моделей 24.09.2026, и смысл у него
     * ровно один — деньги. Служебные вызовы клиент не видит: один отвечает
     * «да» или «нет», другой — одним словом из трёх. Качество письма там
     * не нужно вовсе, а стоят они столько же, сколько ответ клиенту, и на
     * каждый ответ приходится до двух таких вызовов. Разница между самой
     * дорогой и самой дешёвой моделью поколения — двадцатикратная.
     *
     * Почему `null`, а не строка с той же моделью по умолчанию: строка
     * означала бы «выбрано так», и при смене основной модели служебная
     * осталась бы прежней молча. `null` означает «не выбирали» и следует
     * за основной, что и есть прежнее поведение.
     */
    serviceModel: z.string().trim().min(1).max(100).nullable(),
    temperature: z.number().min(0).max(2),
    maxOutputTokens: z.number().int().min(64).max(32_000),
    /**
     * Предел проходов внутри одного ответа. Прямое лекарство от болезни
     * старой системы: без предела спор агента с проверяющим ничем не
     * ограничен, кроме терпения клиента и размера счёта.
     */
    maxIterations: z.number().int().min(1).max(25),
    /**
     * Напоминания замолчавшему клиенту.
     *
     * `delaysMinutes` — паузы перед каждым напоминанием, считая от
     * последнего сообщения агента. Длина списка и есть число напоминаний;
     * на последнее берётся промпт роли `followup_last`, на остальные —
     * `followup_step`. Промпта нет — напоминание не отправляется: писать
     * клиенту без заданного текста система не станет.
     *
     * Выключено по умолчанию, и это решение, а не осторожность.
     * Напоминание — сообщение, которого клиент не просил; включать такое
     * молча нельзя. Паузы при этом заполнены разумными значениями, чтобы
     * включение стоило одного нажатия, а не подбора чисел.
     */
    followUp: z.object({
        enabled: z.boolean(),
        delaysMinutes: z.array(z.number().int().min(5).max(20_160)).max(5),
        /**
         * Последняя попытка — отсчитывается от **первого ответа клиента**,
         * а не от последней реплики агента, и срабатывает один раз.
         *
         * Это «последний шанс» до закрытия суточного окна WhatsApp: после
         * него написать первым уже нельзя без платного шаблона. Отдельный
         * отсчёт нужен именно поэтому — окно живёт от первого ответа
         * клиента, а обычные шаги от последнего нашего сообщения, и
         * привязать их к одной точке нельзя.
         *
         * Взято из базы знаний старой системы (`step_last_minutes`), где
         * стоит 1380 минут — 23 часа. Ноль означает «не отправлять».
         */
        lastStepMinutes: z.number().int().min(0).max(20_160),
        /**
         * Сбрасывать цепочку, когда клиент ответил.
         *
         * `true` — замолчал после третьего напоминания, и счёт пошёл с
         * первого. `false` — цепочка продолжается с того шага, на котором
         * остановилась, и после трёх напоминаний больше ни одного.
         */
        repeatable: z.boolean(),
        /**
         * Окно отправки: часы и дни недели по времени клиента.
         *
         * Без него напоминание уходит в три часа ночи. У старой системы
         * окно есть с самого начала (10:00–19:00, Пн–Пт), у нас не было
         * вовсе — нашлось при разборе их базы знаний 25.09.2026.
         *
         * Часы задаются как «с 10 до 19»: `startHour` включительно,
         * `endHour` исключительно. Равные значения означают «круглые
         * сутки» — так выражается «окна нет» без отдельного выключателя.
         */
        window: z.object({
            startHour: z.number().int().min(0).max(23),
            endHour: z.number().int().min(0).max(24),
            /** 1 — понедельник, 7 — воскресенье, как в ISO. Пусто — все дни. */
            weekdays: z.array(z.number().int().min(1).max(7)).max(7),
            /**
             * Часовой пояс клиента в виде имени IANA: `America/Guatemala`.
             * Не смещение в часах — смещение меняется дважды в год, и
             * записанное числом однажды окажется неверным.
             */
            timezone: z.string().trim().max(64),
        }),
    }),
    /** Сколько ждать продолжения, если человек пишет частями. */
    debounceMs: z.number().int().min(0).max(120_000),
    history: historyModeSchema,
    /**
     * Разбивка ответа на части с паузой между ними — чтобы не выглядеть роботом.
     *
     * Пауза создаёт окно, в которое клиент успевает написать. Что делать в этом
     * случае, расписано в `docs/DECISIONS.md`, раздел «Разбивка ответа и что
     * делать, если клиент ответил в середине». Коротко, чтобы не искать:
     * входящее отменяет неотправленные части; в историю пишется только то, что
     * действительно ушло, и пишется после подтверждения отправки, а не до;
     * части считаются за один ответ, а не за N сообщений.
     *
     * Реализация — шаг 1.4. По умолчанию выключено: две отправки вместо одной
     * дают два шанса на сбой шлюза, и порядок доставки при параллельной
     * отправке не гарантирован.
     */
    splitReply: z.object({
        enabled: z.boolean(),
        delayMs: z.number().int().min(0).max(60_000),
    }),
    textBefore: z.string().max(1_000),
    textAfter: z.string().max(1_000),
    /** Замены в выводе. Массив, а не объект: порядок применения значим. */
    replacements: z
        .array(z.object({ from: z.string().min(1).max(200), to: z.string().max(200) }))
        .max(50),
    limits: z.object({
        maxMessagesPerConversation: z.number().int().min(1).max(500),
        maxCostPerConversationUsd: z.number().min(0).max(100),
    }),
    /** Разрешённые инструменты по именам. Пустой список — инструментов нет. */
    tools: z.array(z.string().trim().min(1).max(64)).max(50),
    /**
     * Проверка ответа перед отправкой клиенту.
     *
     * Формат конфигурации поднят до 2 именно ради этого поля — первый
     * случай, ради которого механизм переноса и заводился. Старые снимки
     * получают `enabled: false`: это описание **прежнего** поведения,
     * когда гейта не было вовсе. Поставить им `true` значило бы задним
     * числом сказать, что диалог полугодовой давности проходил проверку,
     * которой тогда не существовало.
     */
    validation: z.object({
        enabled: z.boolean(),
        /**
         * Сколько раз переспрашивать модель после отказа. Единица —
         * не осторожность, а вывод из старой системы: там порог пропуска
         * был недостижимо высок, спор агента с проверяющим шёл без предела
         * и съел пятикратный бюджет за три дня. Одна поправка — это ровно
         * одна лишняя генерация в худшем случае.
         */
        maxAttempts: z.number().int().min(1).max(3),
        /**
         * Предел длины ответа в знаках. Не то же самое, что предел канала:
         * в WhatsApp влезает 4096, но ответ на четыре тысячи знаков читать
         * никто не станет, и продавец так не пишет.
         */
        maxLength: z.number().int().min(50).max(4_096),
        /**
         * Запрещённые обороты. Проверяются без модели и без учёта регистра —
         * это дёшево и ловит то, что стоит дороже всего: обещание сроков,
         * гарантий и скидок, которых никто не давал.
         */
        forbidden: z.array(z.string().trim().min(1).max(100)).max(50),
        /**
         * Порог оценки, ниже которого ответ не пропускается.
         *
         * Проверяющая модель возвращает оценку от 0 до 10 и список правок.
         * Так устроена проверка в старой системе: там порог равен десяти —
         * любое замечание отправляет ответ на второй круг, и их чеклист
         * считает нормой одобрение с первой–третьей попытки.
         *
         * **Ноль означает «шкалу не применять»**: решает явный вердикт
         * модели, как было у нас до 25.09.2026. Это значение по умолчанию,
         * и выбрано оно не из осторожности, а потому что порог нельзя
         * угадывать: у нас нет ни одного живого прогона, на котором видно
         * распределение оценок. Оценка при этом пишется в шаг прогона
         * всегда — чтобы распределение появилось и порог можно было
         * выбрать по нему, а не по чутью.
         *
         * Второй круг стоит денег и, как выяснилось 24.09.2026, может
         * кончиться молчанием клиента. Поэтому шкала включается осознанно.
         */
        minScore: z.number().int().min(0).max(10),
    }),
});
/**
 * Значения по умолчанию — константой, не внутри схемы.
 *
 * Разница принципиальная, см. комментарий к `AGENT_CONFIG_VERSION`:
 * значения из этой константы попадают в строку при создании версии,
 * поэтому строка полна на момент записи и не зависит от того, что мы
 * будем считать разумным через полгода.
 *
 * Два значения согласованы между собой и меняться должны вместе:
 * `history.count` и `limits.maxMessagesPerConversation`, оба по 40.
 * При них агент видит переписку целиком — просто диалог не может
 * вырасти длиннее. Режим `all` существует и работает; он не выбран по
 * умолчанию потому, что история оплачивается заново при каждом ответе:
 * в диалоге на сто сообщений сто первый ответ тащит за собой все сто,
 * и расход растёт квадратично длине. Переключая на `all`, нужно
 * поднимать и `maxCostPerConversationUsd`, иначе агент упрётся в потолок
 * стоимости раньше, чем клиент договорит. Решение от 22.09.2026:
 * оставить как есть, причину записать.
 */
export const DEFAULT_AGENT_CONFIG = {
    /**
     * Модель по умолчанию — `gpt-6-sol`. Выбрана не рассуждением, а
     * проверкой 23.09.2026: это первая из доступных аккаунту моделей,
     * принявшая строгую схему с границами целых чисел. Чем различаются
     * `sol`, `luna` и `astra` внутри поколения, из каталога не следует.
     *
     * Подтверждено сравнением 24.09.2026 на живом разговоре из пяти ходов:
     * `luna` дешевле в пятнадцать раз и все запреты соблюдает, но не
     * называет прежнюю цену рядом с нынешней и пишет тяжелее. При заказе
     * в $35 разница в цене моделей отбивается приростом конверсии
     * в сотые доли процента, так что `sol` остаётся.
     */
    model: 'gpt-6-sol',
    /** Не выбрана — служебные вызовы идут на основной модели. */
    serviceModel: null,
    temperature: 0.7,
    maxOutputTokens: 1_024,
    maxIterations: 7,
    debounceMs: 8_000,
    /** Час и сутки: первое напоминание в тот же день, второе на следующий. */
    /**
     * Значения по умолчанию взяты из рабочего сценария старой системы:
     * 25 минут, 3 часа, 6 часов, финальное через 23 часа, окно 10:00–19:00.
     * Дни недели пусты — все: у них Пн–Пт, но их рассылки идут по базе,
     * а к нам клиент приходит сам и в субботу тоже.
     */
    followUp: {
        enabled: false,
        delaysMinutes: [25, 180, 360],
        lastStepMinutes: 1_380,
        repeatable: true,
        window: { startHour: 10, endHour: 19, weekdays: [], timezone: 'America/Guatemala' },
    },
    history: { kind: 'lastMessages', count: 40 },
    splitReply: { enabled: false, delayMs: 1_500 },
    textBefore: '',
    textAfter: '',
    replacements: [],
    limits: { maxMessagesPerConversation: 40, maxCostPerConversationUsd: 1 },
    tools: [],
    /**
     * Для новых агентов проверка включена. Детерминированные проверки
     * стоят ноль и ловят самое обидное — пустой ответ, обрыв, незаполненную
     * подстановку. Проверка моделью включается сама, когда к агенту
     * привязан промпт роли `validation`, и не раньше: без текста ей нечем
     * проверять.
     */
    /**
     * Порог десять и две поправки — настройки старой системы, взятые как
     * есть по решению заказчицы 25.09.2026.
     *
     * Десять означает, что любое замечание отправляет ответ на второй
     * круг. Две поправки — их же норма: «супервайзер одобряет сообщение
     * с 1–3 попытки, если требуется больше — вероятен конфликт
     * инструкций». Три попытки это две поправки.
     *
     * Цена известна и принята: почти каждый ответ идёт на второй круг,
     * то есть примерно вдвое дороже. Их система так отработала пятнадцать
     * тысяч задач.
     */
    validation: { enabled: true, maxAttempts: 2, maxLength: 1_200, forbidden: [], minScore: 10 },
};
/**
 * Частичная конфигурация — то, что приходит из формы правки.
 *
 * Не `agentConfigSchema.partial()`: `.partial()` в Zod действует только на
 * верхний уровень, поэтому «поменять один лимит» требовало бы прислать
 * второй лимит заодно. Это прямо противоречит слиянию, которое как раз и
 * умеет накладывать вложенные объекты на один уровень, — и заметно такое
 * расхождение только на живом запросе: типы сходятся, схема собирается,
 * а форма получает отказ «ожидалось число».
 *
 * `history` остаётся целым намеренно: это размеченное объединение, и
 * половина варианта смысла не имеет.
 */
export const agentConfigPatchSchema = agentConfigSchema.partial().extend({
    splitReply: agentConfigSchema.shape.splitReply.partial().optional(),
    followUp: agentConfigSchema.shape.followUp.partial().optional(),
    limits: agentConfigSchema.shape.limits.partial().optional(),
    validation: agentConfigSchema.shape.validation.partial().optional(),
});
/**
 * Какой моделью делать служебный вызов.
 *
 * Функция, а не чтение поля на месте: `null` здесь значит «та же, что
 * отвечает клиенту», и разворачивать это значение в трёх местах —
 * верный способ однажды развернуть его по-разному. Заодно место, куда
 * смотреть, когда возникнет вопрос «а почему проверка шла не на той
 * модели».
 */
export function serviceModelOf(config) {
    return config.serviceModel ?? config.model;
}
export const AGENT_CONFIG_FIELD_LABELS = {
    model: 'Модель',
    serviceModel: 'Модель для служебных вызовов',
    temperature: 'Температура',
    maxOutputTokens: 'Предел ответа в токенах',
    maxIterations: 'Предел проходов',
    debounceMs: 'Дебаунс, мс',
    'followUp.enabled': 'Напоминать, если клиент замолчал',
    'followUp.delaysMinutes': 'Паузы перед напоминаниями, мин',
    'followUp.lastStepMinutes': 'Последнее напоминание через, мин от первого ответа',
    'followUp.repeatable': 'Начинать цепочку заново, когда клиент ответил',
    'followUp.window.startHour': 'Напоминать не раньше, час',
    'followUp.window.endHour': 'Напоминать не позже, час',
    'followUp.window.weekdays': 'Дни недели для напоминаний',
    'followUp.window.timezone': 'Часовой пояс клиента',
    'history.kind': 'История: режим',
    'history.count': 'История: сообщений',
    'history.minutes': 'История: минут',
    'splitReply.enabled': 'Разбивать ответ',
    'splitReply.delayMs': 'Пауза между частями, мс',
    textBefore: 'Текст до сообщения',
    textAfter: 'Текст после сообщения',
    replacements: 'Замены в выводе',
    'limits.maxMessagesPerConversation': 'Максимум сообщений на диалог',
    'limits.maxCostPerConversationUsd': 'Максимальная стоимость диалога, $',
    tools: 'Инструменты',
    'validation.enabled': 'Проверять ответ перед отправкой',
    'validation.maxAttempts': 'Попыток исправить',
    'validation.maxLength': 'Предел длины ответа, знаков',
    'validation.forbidden': 'Запрещённые обороты',
    'validation.minScore': 'Пропускать с оценкой не ниже',
};
// ─── агенты ───
export const agentPromptBindingSchema = z.object({
    role: promptRoleSchema,
    promptId: z.uuid(),
    promptName: z.string(),
    promptVersionId: z.uuid(),
    promptVersion: z.number().int().positive(),
});
export const agentVersionSummarySchema = z.object({
    id: z.uuid(),
    agentId: z.uuid(),
    version: z.number().int().positive(),
    configVersion: z.number().int().positive(),
    note: z.string(),
    createdByUserId: z.uuid().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.iso.datetime(),
    isPublished: z.boolean(),
    prompts: z.array(agentPromptBindingSchema),
});
export const agentVersionSchema = agentVersionSummarySchema.extend({
    config: agentConfigSchema,
});
export const agentSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string(),
    publishedVersion: agentVersionSummarySchema.nullable(),
    latestVersion: z.number().int().nonnegative(),
    versionCount: z.number().int().nonnegative(),
    /** Каналы, к которым привязан агент. Пусто — агент ни за что не отвечает. */
    channelNames: z.array(z.string()),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const agentDetailSchema = agentSchema.extend({
    versions: z.array(agentVersionSummarySchema),
    /** Опубликованная конфигурация, либо последняя созданная. */
    currentConfig: agentConfigSchema,
    currentVersionId: z.uuid().nullable(),
});
export const promptBindingInputSchema = z.object({
    role: promptRoleSchema,
    /**
     * Именно версия промпта, а не промпт. Разница — в комментарии к файлу:
     * ссылка на промпт означала бы, что правка текста меняет поведение
     * задним числом.
     */
    promptVersionId: z.uuid(),
});
/**
 * Текст системной инструкции прямо в форме создания агента.
 *
 * Появился 23.09.2026 и закрывает неудобство, унаследованное от старой
 * админки: чтобы создать агента, приходилось сперва идти в другой экран
 * и заводить промпт. Требование «агент ссылается на версию промпта»
 * остаётся — из него следует, что версия должна существовать к моменту
 * привязки, но вовсе не следует, что создавать её обязан человек руками
 * в отдельном месте. Сервер заводит промпт, первую версию и публикует
 * её сам, одной транзакцией с созданием агента.
 *
 * Имя промпта необязательно: пустое — значит взять имя агента. Два
 * объекта с одним именем не путаница, а ровно то, чего ожидаешь, когда
 * завёл их одним действием.
 */
export const inlinePromptInputSchema = z.object({
    body: z.string().min(1).max(PROMPT_BODY_MAX),
    name: z.string().trim().max(120).default(''),
});
export const createAgentInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).default(''),
    /** Пусто — берутся значения по умолчанию целиком. */
    config: agentConfigPatchSchema.default({}),
    prompts: z.array(promptBindingInputSchema).max(PROMPT_ROLES.length).default([]),
    /** Новый промпт текстом. Взаимоисключим с привязкой роли `system`. */
    systemPrompt: inlinePromptInputSchema.optional(),
    note: z.string().trim().max(300).default(''),
    publish: z.boolean().default(false),
})
    .refine((value) => !(value.systemPrompt && value.prompts.some((binding) => binding.role === 'system')), {
    message: 'Выберите либо существующий промпт, либо текст новой инструкции — не оба сразу',
    path: ['systemPrompt'],
});
export const updateAgentInputSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).optional(),
});
export const createAgentVersionInputSchema = z.object({
    /**
     * Частичная конфигурация: приходит только изменённое, остальное берётся
     * из версии, от которой правят. Так форма может отправлять одно поле,
     * а не пересылать объект целиком, — и не может случайно затереть то,
     * чего не показывала.
     */
    config: agentConfigPatchSchema.default({}),
    prompts: z.array(promptBindingInputSchema).max(PROMPT_ROLES.length).optional(),
    note: z.string().trim().max(300).default(''),
    publish: z.boolean().default(false),
});
export const agentVersionResultSchema = z.object({
    version: agentVersionSummarySchema,
    created: z.boolean(),
    published: z.boolean(),
});
// ─── сравнение версий ───
export const diffQuerySchema = z.object({ from: z.uuid(), to: z.uuid() });
export const textDiffLineSchema = z.object({
    kind: z.enum(['equal', 'added', 'removed']),
    fromLine: z.number().int().positive().nullable(),
    toLine: z.number().int().positive().nullable(),
    text: z.string(),
});
export const promptDiffSchema = z.object({
    from: promptVersionSummarySchema,
    to: promptVersionSummarySchema,
    lines: z.array(textDiffLineSchema),
    summary: z.object({
        added: z.number().int().nonnegative(),
        removed: z.number().int().nonnegative(),
        identical: z.boolean(),
    }),
});
export const agentDiffSchema = z.object({
    from: agentVersionSummarySchema,
    to: agentVersionSummarySchema,
    /** Изменённые поля конфигурации плоским списком. */
    changes: z.array(z.object({ path: z.string(), label: z.string(), before: z.string(), after: z.string() })),
    /** Изменения привязок промптов — отдельно, они важнее остального. */
    promptChanges: z.array(z.object({
        role: promptRoleSchema,
        before: z.string().nullable(),
        after: z.string().nullable(),
    })),
    identical: z.boolean(),
});
// ─── привязка агента к диалогу ───
/**
 * Какая версия агента закреплена за диалогом.
 *
 * `reason` заполняется, когда версии нет, и отвечает на вопрос «почему
 * бот молчит» одной строкой вместо чтения логов. Тот же приём, что и в
 * гейтах входа на шаге 1.1: отказ обязан объяснять себя.
 */
export const conversationAgentSchema = z.object({
    agentId: z.uuid().nullable(),
    agentName: z.string().nullable(),
    agentVersionId: z.uuid().nullable(),
    agentVersion: z.number().int().positive().nullable(),
    /** Версия закреплена именно этим вызовом, а не раньше. */
    pinnedNow: z.boolean(),
    reason: z.string().nullable(),
});
//# sourceMappingURL=agents.js.map